import { AiScribeGetJobDetailsResponse } from './ai-scribe-service';
import { gatewayService } from './gateway-service';
import { supabase } from './supabase';
import { generateAlphanumericId } from './utils';

export interface SupabaseScribeJob {
  id?: string;
  appointment_id?: string;
  location_id?: string;
  patient_id?: string;
  pt_first_name?: string;
  pt_last_name?: string;
  pt_dob?: string;
  pt_gender?: string;
  pt_email?: string;
  pt_phone_number?: string;
  service?: string;
  appointment_start_time?: string;
  status?: 'CREATED' | 'STARTED' | 'RECORDING' | 'PAUSED' | 'COMPLETED';
  created_by?: string;
  audio_file_key?: string;
  transcript_file_key?: string;
  eyecare_report_key?: string;
  start_time?: string;
  end_time?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Get job details by job ID
 * @param jobId - The ID of the job to get details for
 * @returns
 */
async function getJobDetailsByJobId(
  jobId: string
): Promise<AiScribeGetJobDetailsResponse> {
  const result = await gatewayService.getJobDetailsByJobId(jobId);
  return result.data as AiScribeGetJobDetailsResponse;
}

/**
 * Get job details by appointment info
 * @param appointmentId - The ID of the appointment to get details for
 * @param locationId - The ID of the location to get details for
 * @param patientId - The ID of the patient to get details for
 * @returns
 */
async function getJobDetailsByAppointmentInfo(
  appointmentId: string,
  locationId: string,
  patientId: string
): Promise<AiScribeGetJobDetailsResponse> {
  const result = await gatewayService.getJobDetailsByAppointmentInfo(
    appointmentId,
    locationId,
    patientId
  );
  return result.data as AiScribeGetJobDetailsResponse;
}

/**
 * Create a new scribe job
 * @param job - The job to create
 * @param locationId - The ID of the location to create the job for
 * @param user - The user creating the job
 * @returns The created job
 */
async function createJob(
  job: SupabaseScribeJob,
  locationId: string,
  user: {
    first_name?: string;
    last_name?: string;
    username?: string;
    email?: string;
  }
): Promise<SupabaseScribeJob> {
  if (!locationId || !user) {
    throw new Error('Location or user not available');
  }
  const appointmentStartTime = new Date().toISOString();
  job.id = crypto.randomUUID();
  job.appointment_id = generateAlphanumericId(20);
  job.location_id = locationId;
  job.patient_id = generateAlphanumericId(18);
  job.appointment_start_time = appointmentStartTime;
  job.status = 'CREATED';
  job.created_by =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username || user.email || 'Unknown User';
  job.audio_file_key = '';
  job.transcript_file_key = '';
  job.eyecare_report_key = '';
  console.log('🔍 Creating job:', job);
  const { data, error } = await supabase
    .from('ev_scribe_jobs')
    .insert(job)
    .select()
    .single();
  const scribeJob = data as SupabaseScribeJob;
  console.log('🔍 Created job:', scribeJob);
  if (error) {
    throw new Error(`Failed to create job: ${error.message}`);
  }
  if (!data) {
    throw new Error('No data returned after creating job');
  }
  return scribeJob;
}

/**
 * Update the status of a scribe job
 * @param jobId - The ID of the job to update
 * @param status - The new status to set
 * @returns The updated job
 */
async function updateScribeJobStatus(
  jobId: string,
  status: 'CREATED' | 'STARTED' | 'RECORDING' | 'PAUSED' | 'COMPLETED'
): Promise<void> {
  const result = await gatewayService.updateScribeJobStatus(jobId, status);
  console.log('🔍 Updated job status:', result.data);
  if (!result.success) {
    throw new Error(`Failed to update job status: ${result.error}`);
  }
}

export const AiScribeJobUtilsService = {
  getJobDetailsByJobId,
  getJobDetailsByAppointmentInfo,
  createScribeJob: createJob,
  updateScribeJobStatus,
};
